import React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ActivityResponse, LeaderboardPeriod } from '../../services/gamificationApi';

export const T = {
    primary:      '#005581',
    primaryLight: '#4F99BD',
    primaryGrad:  'linear-gradient(135deg, #4F99BD 0%, #005581 100%)',
    bg:           '#f1f5f9',
    card:         '#ffffff',
    border:       '#e2e8f0',
    text:         '#0f172a',
    muted:        '#475569',
    faint:        '#64748b',
    radius:       '14px',
    font:         "'Source Sans 3', system-ui, -apple-system, sans-serif",
    shadow:       '0 1px 4px rgba(0,0,0,0.06)',
};

export const LEVEL_COLORS: Record<string, { bg: string; text: string }> = {
    Novice:            { bg: '#f1f5f9', text: '#475569' },
    Practitioner:      { bg: '#dbeafe', text: '#1e40af' },
    Proficient:        { bg: '#dcfce7', text: '#166534' },
    Expert:            { bg: '#fef3c7', text: '#92400e' },
    'Master Educator': { bg: '#fee2e2', text: '#991b1b' },
};

// `label` stays as the English source for callers that have not moved to i18n yet;
// translate `labelKey` where the option is rendered.
export const PERIOD_OPTIONS: Array<{ value: LeaderboardPeriod; label: string; labelKey: string }> = [
    { value: 'week',     label: 'This Week',  labelKey: 'myProgress.periodWeek' },
    { value: 'month',    label: 'This Month', labelKey: 'myProgress.periodMonth' },
    { value: 'all_time', label: 'All Time',   labelKey: 'myProgress.periodAllTime' },
];

// Level names arrive from the API in English (`level_name`) and double as lookup keys
// (LEVEL_COLORS); translate only what is shown. Unknown names pass through unchanged.
const LEVEL_LABEL_KEYS: Record<string, string> = {
    Novice:            'myProgress.levelNovice',
    Practitioner:      'myProgress.levelPractitioner',
    Proficient:        'myProgress.levelProficient',
    Expert:            'myProgress.levelExpert',
    'Master Educator': 'myProgress.levelMasterEducator',
};

export function levelLabel(name: string): string {
    const key = LEVEL_LABEL_KEYS[name];
    return key ? i18n.t(key, { defaultValue: name }) : name;
}

// Badge copy lives in utils/badgeCatalogue (English). Translate it where shown,
// keyed by the backend badge key; the catalogue text is the fallback.
const BADGE_I18N_KEYS: Record<string, string> = {
    first_session:         'firstSession',
    streak_5:              'streak5',
    perfect_score:         'perfectScore',
    scenario_explorer:     'scenarioExplorer',
    streak_14:             'streak14',
    level_up_practitioner: 'levelUpPractitioner',
    streak_30:             'streak30',
    streak_50:             'streak50',
    streak_100:            'streak100',
    streak_365:            'streak365',
};

export function badgeName(key: string, fallback: string): string {
    const k = BADGE_I18N_KEYS[key];
    return k ? i18n.t(`myProgress.badgeName.${k}`, { defaultValue: fallback }) : fallback;
}

export function badgeDesc(key: string, fallback: string): string {
    const k = BADGE_I18N_KEYS[key];
    return k ? i18n.t(`myProgress.badgeDesc.${k}`, { defaultValue: fallback }) : fallback;
}

export const MEDALS = ['🥇', '🥈', '🥉'];

export function initials(name: string) {
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export function relativeTime(iso: string | null): string {
    if (!iso) return '—';
    const then = new Date(iso);
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((now.setHours(0, 0, 0, 0) - new Date(then).setHours(0, 0, 0, 0)) / dayMs);
    if (diffDays <= 0) return i18n.t('common.today');
    if (diffDays === 1) return i18n.t('myProgress.yesterday');
    return i18n.t('myProgress.daysAgo', { count: diffDays });
}

// Local calendar date as `YYYY-MM-DD`, built field by field.
//
// `toISOString().slice(0, 10)` looks like the same thing but converts to UTC
// first, so a local midnight in a positive-offset zone (e.g. +05:30) reports the
// previous day.
export function localDateKey(d: Date): string {
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}

// Sessions since the start of the current calendar week (Monday 00:00 local).
// A rolling trailing-7-days window would never reset, so a user who finished a
// week over goal would still see that count on Monday morning. "Weekly Goal"
// implies a fresh start, so the counter has to zero out on the week boundary.
//
// `ActivityDay.date` is a bare calendar date, not a timestamp, so the week
// boundary is compared as a string. `new Date("2026-07-27")` parses date-only
// ISO as UTC midnight, which lands on the previous day in every negative-offset
// zone (including the product default, America/Chicago) and silently drops
// Monday's sessions from the week.
//
// Shared with MyProgressPage, which owns the goal itself: the two surfaces have
// to agree on what "this week" means.
export function sessionsThisCalendarWeek(activity: ActivityResponse | null): number {
    if (!activity) return 0;
    const weekStart = new Date();
    // getDay(): 0=Sun … 6=Sat. Shift back to Monday; Sunday counts as day 7 of the prior week.
    weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
    const startKey = localDateKey(weekStart);
    return activity.days.reduce((sum, d) => (d.date >= startKey ? sum + d.count : sum), 0);
}

// A small uppercase caption above a filter control.
export function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
            <span style={{ fontSize: '0.66rem', fontWeight: 700, color: T.faint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
            {children}
        </div>
    );
}

export const filterInputStyle: React.CSSProperties = {
    fontFamily: T.font, fontSize: '0.8rem', padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${T.border}`, color: T.text, background: '#fff',
    width: '100%', boxSizing: 'border-box',
};

export function RangeInputs({ min, max, onMinChange, onMaxChange }: {
    min: string; max: string; onMinChange: (v: string) => void; onMaxChange: (v: string) => void;
}) {
    const { t } = useTranslation();
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" min={0} value={min} onChange={e => onMinChange(e.target.value)} placeholder={t('myProgress.min')} style={{ ...filterInputStyle, flex: 1, minWidth: 0 }} />
            <span style={{ color: T.faint, fontSize: '0.78rem', flexShrink: 0 }}>–</span>
            <input type="number" min={0} value={max} onChange={e => onMaxChange(e.target.value)} placeholder={t('myProgress.max')} style={{ ...filterInputStyle, flex: 1, minWidth: 0 }} />
        </div>
    );
}

